import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/Button/Button';
import { Alert } from '@/components/Alert/Alert';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { TextField } from '@/components/TextField/TextField';
import { ApiError, NetworkError } from '@/lib/api';
import { currentLocale, t } from '@/lib/i18n';
import { useCreateLibraryItem, useUpdateLibraryItem } from '../hooks';
import {
  itemFormSchema,
  type ItemFormValues,
  type LibraryItemDetail,
  type LibraryKind,
} from '../schemas';
import { UsedByLinks } from './UsedByLinks';
import styles from './LibraryItemForm.module.css';

type TenantDetail = Extract<LibraryItemDetail, { kind: 'tenant' }>;

interface LibraryItemFormProps {
  kind: LibraryKind;
  /** Editing existing tenant item, or undefined for create. */
  initial?: TenantDetail;
  /** Optional banner shown above the form (e.g., after customizing from a system row). */
  banner?: {
    customizedFrom: string;
    migratedMemberCount: number;
  };
}

export function LibraryItemForm({ kind, initial, banner }: LibraryItemFormProps) {
  const navigate = useNavigate();
  const create = useCreateLibraryItem(kind);
  const update = useUpdateLibraryItem(kind);
  const [formError, setFormError] = useState<string | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const form = useForm<ItemFormValues>({
    resolver: zodResolver(itemFormSchema),
    defaultValues: {
      name: initial?.name ?? '',
      description: initial?.description ?? '',
    },
  });

  const backPath = `/library/${kind === 'position' ? 'positions' : 'units'}`;
  const labels = labelsFor(kind, Boolean(initial));

  function onSubmit(values: ItemFormValues) {
    setFormError(null);
    if (initial) {
      update.mutate(
        {
          id: initial.id,
          patch: {
            name: values.name,
            description: values.description?.trim() ? values.description : null,
          },
        },
        {
          onSuccess: () => void navigate(backPath),
          onError: (err) => setFormError(formatError(err)),
        },
      );
    } else {
      create.mutate(values, {
        onSuccess: () => void navigate(backPath),
        onError: (err) => setFormError(formatError(err)),
      });
    }
  }

  const isPending = create.isPending || update.isPending;

  const showBanner = banner && !bannerDismissed;
  const bannerMessage = banner ? formatBannerMessage(banner) : '';

  return (
    <div className={styles.page}>
      <PageHeader title={labels.title} subtitle={labels.subtitle} />

      {showBanner ? (
        <Alert variant="info" onDismiss={() => setBannerDismissed(true)}>
          {bannerMessage}
        </Alert>
      ) : null}

      <form
        className={styles.form}
        onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}
        noValidate
      >
        <TextField
          label={t('Name')}
          autoComplete="off"
          error={form.formState.errors.name?.message}
          {...form.register('name')}
        />

        <div className={styles.field}>
          <label htmlFor="library-description" className={styles.label}>
            {t('Description')}
          </label>
          <textarea
            id="library-description"
            rows={3}
            className={styles.textarea}
            {...form.register('description')}
          />
          <div className={styles.hint}>{t('Optional.')}</div>
        </div>

        {formError ? <Alert variant="error">{formError}</Alert> : null}

        {initial ? (
          <UsedByLinks
            agents={initial.defaultedByAgents}
            jobs={initial.defaultedByJobs}
            kindLabel={kind === 'position' ? t('Position') : t('Department')}
          />
        ) : null}

        <div className={styles.actions}>
          <Button type="button" variant="ghost" size="md" onClick={() => void navigate(backPath)}>
            {t('Cancel')}
          </Button>
          <Button type="submit" variant="primary" size="md" isLoading={isPending}>
            {isPending ? t('Saving…') : t('Save')}
          </Button>
        </div>
      </form>
    </div>
  );
}

function formatBannerMessage(banner: {
  customizedFrom: string;
  migratedMemberCount: number;
}): string {
  if (banner.migratedMemberCount <= 0) {
    return t('Customized from "{name}". You can now edit it.', {
      name: banner.customizedFrom,
    });
  }
  const pluralRules = new Intl.PluralRules(currentLocale());
  const isOne = pluralRules.select(banner.migratedMemberCount) === 'one';
  const message = isOne
    ? t('Customized from "{name}". {n} Member assignment was moved to your copy.', {
        name: banner.customizedFrom,
        n: String(banner.migratedMemberCount),
      })
    : t('Customized from "{name}". {n} Member assignments were moved to your copy.', {
        name: banner.customizedFrom,
        n: String(banner.migratedMemberCount),
      });
  return message;
}

function formatError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 409) {
      return t('A similar name already exists. Pick a different name.');
    }
    if (err.status === 403) return t("You don't have permission to edit the Library.");
    if (err.status === 404) return t('This item is no longer available.');
    return err.message;
  }
  if (err instanceof NetworkError) {
    return t("Couldn't reach the server. Check your connection and try again.");
  }
  return t('Something went wrong. Please try again.');
}

function labelsFor(kind: LibraryKind, isEdit: boolean): { title: string; subtitle: string } {
  if (kind === 'position') {
    return {
      title: isEdit ? t('Edit Position') : t('New Position'),
      subtitle: isEdit
        ? t('Update the display name and description.')
        : t('Add a new Position to your Library.'),
    };
  }
  return {
    title: isEdit ? t('Edit Department') : t('New Department'),
    subtitle: isEdit
      ? t('Update the display name and description.')
      : t('Add a new Department to your Library.'),
  };
}
